import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class BeerflexSettingsCalculated extends ModelObject<BeerflexSettingsCalculated> {

    public ftpPasswordLength: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ftpPasswordLength = jsonSerializedObject.ftpPasswordLength;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ftpPasswordLength = this.ftpPasswordLength;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ftpPasswordLength", rule: (v: any) => v == null || v >= -2147483648 || "FtpPasswordLength is not greater than minimum allowed value: -2147483648"},
            {field: "ftpPasswordLength", rule: (v: any) => v == null || v <= 2147483647 || "FtpPasswordLength is above maximum allowed value: 2147483647"},
    ];
}


export class BeerflexSettings extends ModelObject<BeerflexSettings> {

    public version: number = 0;
    /**
    * The Beerflex FTP server address, often ftp://ftp.siba.co.uk.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public ftpServerAddress: string = "";
    /**
    * The username for connecting to the Beerflex FTP server.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public ftpUsername: string = "";
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    public calculated: BeerflexSettingsCalculated = new BeerflexSettingsCalculated();
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * Orders placed on Beerflex before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    /**
    * When true, delivery confirmations will be sent to Beerflex for orders which have been signed for by customer, regardless of whether the products delivered match those originally ordered on BeerFlex.
    */
    public sendConfirmationIfAlternativesDelivered: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.ftpServerAddress = jsonSerializedObject.ftpServerAddress;
          this.ftpUsername = jsonSerializedObject.ftpUsername;
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.calculated = new BeerflexSettingsCalculated(jsonSerializedObject.calculated);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.sendConfirmationIfAlternativesDelivered = jsonSerializedObject.sendConfirmationIfAlternativesDelivered;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.ftpServerAddress = this.ftpServerAddress;
        toRet.ftpUsername = this.ftpUsername;
        toRet.runInBackground = this.runInBackground;
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.sendConfirmationIfAlternativesDelivered = this.sendConfirmationIfAlternativesDelivered;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "ftpServerAddress", rule: (v: any) => v == null || v.length <= 1000 || "FtpServerAddress has too many characters (1000)"},
            {field: "ftpUsername", rule: (v: any) => v == null || v.length <= 1000 || "FtpUsername has too many characters (1000)"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class OutletMapping extends ModelObject<OutletMapping> {

    /**
    * The Beerlex property number.
    * Not Nullable
    */
    public beerflexPropertyNumber: string = "";
    /**
    * The Brewman outlet id the Beerflex property is mapped to.
    */
    public brewmanOutletId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.beerflexPropertyNumber = jsonSerializedObject.beerflexPropertyNumber;
          this.brewmanOutletId = Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.beerflexPropertyNumber = this.beerflexPropertyNumber;
        toRet.brewmanOutletId = this.brewmanOutletId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanOutletId", rule: (v: any) => !v.isEmpty() || "OutletMapping.BrewmanOutletId cannot be empty"},
    ];
}


export class ProductMapping extends ModelObject<ProductMapping> {

    /**
    * The Beerlex code for a product.
    * Not Nullable
    */
    public beerflexProductCode: string = "";
    /**
    * The Brewman invoiceable item id the Beerflex product is mapped to.
    */
    public brewmanInvoiceableItemId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.beerflexProductCode = jsonSerializedObject.beerflexProductCode;
          this.brewmanInvoiceableItemId = Guid.parseOrEmpty(jsonSerializedObject.brewmanInvoiceableItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.beerflexProductCode = this.beerflexProductCode;
        toRet.brewmanInvoiceableItemId = this.brewmanInvoiceableItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanInvoiceableItemId", rule: (v: any) => !v.isEmpty() || "ProductMapping.BrewmanInvoiceableItemId cannot be empty"},
    ];
}


export class BeerflexSettingsService extends ServiceBase {

  public async getBeerflexSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BeerflexSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new BeerflexSettings(response.beerflexSettings);;
    return this.makeJsonRequest("beerflexSettings/v1/getBeerflexSettings", req, false, extractor, opts);
  };

  public async saveBeerflexSettings(beerflexSettings: BeerflexSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    beerflexSettings:beerflexSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("beerflexSettings/v1/saveBeerflexSettings", req, true, extractor, opts);
  };

  public async saveBeerflexFtpPassword(ftpPassword: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ftpPassword:ftpPassword,
    };
    const extractor = null;
    return this.makeJsonRequest("beerflexSettings/v1/saveBeerflexFtpPassword", req, true, extractor, opts);
  };

  public async getOutletMappings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletMapping)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.outletMappings.map((v: any) => new OutletMapping(v));;
    return this.makeJsonRequest("beerflexSettings/v1/getOutletMappings", req, false, extractor, opts);
  };

  public async assignOutletMapping(beerflexPropertyNumber: string, brewmanOutletId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    beerflexPropertyNumber:beerflexPropertyNumber,
    brewmanOutletId:brewmanOutletId == null ? null : brewmanOutletId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("beerflexSettings/v1/assignOutletMapping", req, true, extractor, opts);
  };

  public async getProductMappings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductMapping)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.productMappings.map((v: any) => new ProductMapping(v));;
    return this.makeJsonRequest("beerflexSettings/v1/getProductMappings", req, false, extractor, opts);
  };

  public async assignProductMapping(beerflexProductCode: string, brewmanInvoiceableItemId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    beerflexProductCode:beerflexProductCode,
    brewmanInvoiceableItemId:brewmanInvoiceableItemId == null ? null : brewmanInvoiceableItemId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("beerflexSettings/v1/assignProductMapping", req, true, extractor, opts);
  };
}

export const beerflexSettingsService = new BeerflexSettingsService();